import { useRef, useCallback, useState } from 'react';
import Webcam from 'react-webcam';
import { Camera, RotateCcw, ArrowLeft, Loader2, ShieldCheck, AlertTriangle, Home, Pencil, ImagePlus, CameraOff } from 'lucide-react';
import { fileToDataUrl } from '../../lib/image';
import { Link } from 'react-router-dom';
import { ResendLinkCard } from './ResendLinkCard';
import { toast } from 'sonner';

export const WebcamCaptureStep = ({ capturedImage, onCapture, onRetake, onBack, onSubmit, loading, error, homePath, email, slug }) => {
  const webcamRef = useRef(null);
  const [camError, setCamError] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const dataUrl = await fileToDataUrl(file);
      setUploaded(true);
      onCapture(dataUrl);
      toast.success('Selfie added');
    } catch {
      toast.error('Could not read that image');
    } finally {
      e.target.value = '';
    }
  };

  const capture = useCallback(() => {
    const shot = webcamRef.current?.getScreenshot();
    if (!shot) {
      toast.error('Camera not ready yet');
      return;
    }
    onCapture(shot);
    toast.success('Photo captured');
  }, [onCapture]);

  return (
    <div className="card-brutal fade-up" data-testid="photo-capture-card">
      <div className="px-6 py-5 border-b-2 border-line flex items-start justify-between gap-4">
        <div>
          <h2 className="h-display text-xl">Unlock your album with a selfie</h2>
          <p className="text-sm text-slate-400 mt-1">Look straight at the camera in good lighting.</p>
        </div>
        <span className="badge-lime shrink-0 hidden sm:inline-flex"><ShieldCheck className="w-3 h-3" /> Not stored</span>
      </div>
      <div className="p-6 space-y-5">
        <div className="relative overflow-hidden bg-black border-2 border-line aspect-video">
          {!capturedImage ? (
            camError ? (
              <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="camera-unavailable">
                <CameraOff className="w-8 h-8 text-slate-500" />
                <p className="font-display font-bold uppercase tracking-wider text-sm text-white">Camera unavailable</p>
                <p className="text-xs text-slate-400 max-w-xs">Your browser blocked the camera or none was found. Upload a selfie from your phone instead.</p>
                <label htmlFor="selfie-file-input" className="btn-primary !py-2.5 !text-xs cursor-pointer mt-1" data-testid="upload-selfie-fallback-btn">
                  <ImagePlus className="w-4 h-4" /> Upload a selfie
                </label>
              </div>
            ) : (
              <Webcam
                audio={false}
                ref={webcamRef}
                screenshotFormat="image/jpeg"
                className="w-full h-full object-cover"
                videoConstraints={{ facingMode: 'user', width: 1280, height: 720 }}
                onUserMediaError={() => setCamError(true)}
                mirrored
              />
            )
          ) : (
            <img src={capturedImage} alt="Captured" className="w-full h-full object-cover" style={{ transform: uploaded ? 'none' : 'scaleX(-1)' }} />
          )}
          <input id="selfie-file-input" data-testid="selfie-file-input" type="file" accept="image/*" capture="user" className="hidden" onChange={onFile} />
          {!(camError && !capturedImage) && (
            <>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-44 h-56 sm:w-52 sm:h-64 rounded-[50%] border-2 border-cyan shadow-[0_0_0_9999px_rgba(40,35,52,0.45)]" />
              </div>
              <span className="absolute top-3 left-3 badge-cyan !bg-ink/80">{capturedImage ? 'Preview' : 'Live'}</span>
            </>
          )}
        </div>

        {!capturedImage && !camError && (
          <label htmlFor="selfie-file-input" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 hover:text-cyan cursor-pointer transition-colors" data-testid="upload-selfie-link">
            <ImagePlus className="w-3.5 h-3.5" /> No camera? Upload a selfie instead
          </label>
        )}

        <p className="font-mono text-[11px] text-slate-500 leading-relaxed">
          This selfie is only used to find you in the photographers' shots. It becomes a 128-number signature and is discarded immediately — the image itself is never saved.
        </p>

        {error && (
          <div className="border-2 border-red-500 bg-red-500/10 p-4" data-testid="register-error">
            <p className="flex items-start gap-2 text-sm text-red-300"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {error}</p>
            {/already registered/i.test(error) && (
              <div className="mt-3 pl-6" data-testid="already-registered-resend">
                <p className="text-xs text-slate-400 mb-3">Good news — your album already exists. We can email you the link:</p>
                <ResendLinkCard slug={slug} initialEmail={email} compact />
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-2 mt-3 pl-6">
              <button data-testid="edit-details-btn" onClick={onBack} className="btn-ghost !py-2 !text-[11px]"><Pencil className="w-3.5 h-3.5" /> Edit details</button>
              <Link to={homePath} data-testid="back-home-btn" className="btn-ghost !py-2 !text-[11px]"><Home className="w-3.5 h-3.5" /> Back to event home</Link>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button data-testid="back-btn" onClick={onBack} className="btn-ghost sm:flex-none !py-3">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          {!capturedImage ? (
            <button data-testid="capture-photo-btn" onClick={capture} className="btn-primary flex-1">
              <Camera className="w-4 h-4" /> Capture photo
            </button>
          ) : (
            <>
              <button data-testid="retake-photo-btn" onClick={() => { setUploaded(false); onRetake(); }} className="btn-ghost flex-1 !py-3">
                <RotateCcw className="w-4 h-4" /> Retake
              </button>
              <button data-testid="register-btn" onClick={onSubmit} disabled={loading} className="btn-lime flex-1">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? 'Registering…' : 'Complete registration'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
